import * as _ from 'underscore';
import { ITextDropdownOption } from '../directives/tbTextDropdown';
import { DateHelpers } from '../helpers/DateHelpers';
import { Helpers } from '../helpers/Helpers';
import { SiteRoleFilterHelper } from '../helpers/SiteRoleFilterHelper';
import { BackgroundJobStatus, BackgroundJobTaskType, BackgroundJobTimeRange } from '../../backgroundJob/types/BackgroundJobInfo';
import { ContentFilterKeyName } from '../models/ContentFilters';
import { FlowOutputStepRunStatus } from '../models/FlowOutputStepRunInfo';
import { Module } from '../Module';
import { SiteRolesResource } from '../../userManagement/services/resources/SiteRoles';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { FilterFieldNames } from '../ServerApi/types/IFilterClause';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { DataAccelerationStatus } from '../models/DataAccelerationStatusInfo';
import { FeatureFlags } from '../FeatureFlags';
import { CollectionVisibility } from '../../app/spaces/Collections/CollectionModels';
import { FilterValueConstants } from './FilterValueConstants';

export type FilterCategory = 'general' | 'datasource' | 'project' | 'dataAcceleration';
export type FilterType = 'typeahead' | 'boolean' | 'text' | 'textDropdown' | 'date' | 'select';

export interface IFilterDefinition<TFilterValue> {
  name: ContentFilterKeyName;
  type: FilterType;
  category: FilterCategory;

  // These functions map a filter value to and from $location.search() params.
  valueToParams: (value: TFilterValue) => any;
  valueFromParams: (params: any) => ng.IPromise<TFilterValue>;
}

export interface ITypeaheadFilterDefinition extends IFilterDefinition<IFieldValue> {
  field: string;
  matchPrefixesOnly: boolean;
}

export interface ISelectFilterDefinitionOption<TFilterValue> {
  translate: string;
  value: TFilterValue;
  count?: number;
}

export interface ISelectFilterDefinition<TFilterValue> extends IFilterDefinition<TFilterValue> {
  options: ISelectFilterDefinitionOption<TFilterValue>[];
}

interface ITextDropdownFilterDefition<TFilterValue> extends ISelectFilterDefinition<TFilterValue> {
  isValid: (val: string) => boolean;
}

interface ISelectFilterDefinitionMap<TFilterValue> extends _.Dictionary<TFilterValue> {
  '': TFilterValue; //This is the required default select option value
}

export enum PasswordStatus { Any, Embedded, NotEmbedded }
export enum DatasourceType { All = 1, Embedded = 2, Published = 3 }
export enum ExtractStatus {
  All = '',
  Live = 'live',
  Published = 'published',  // Currently only the workbook filter supports this option
  AllExtracts = 'allExtracts',
  UnencryptedExtracts = 'unencryptedExtracts',
  EncryptedExctacts = 'encryptedExtracts',
  CurrentlyEncrypting = 'currentlyEncrypting',
  CurrentlyDecrypting = 'currentlyDecrypting'
}

export class FilterPanelService {

  private ServerService: SessionManagementService;
  public static $inject = [
    'SearchSuggestionsService',
    '$q'
  ];

  private filterDefinitions: { [N in ContentFilterKeyName]: IFilterDefinition<any> };

  constructor(
    private suggestions: SearchSuggestionsService,
    private $q: ng.IQService) {
    this.ServerService = getSessionManagementServiceInstance();
    this.createFilterDefinitions();
  }

  private createFilterDefinitions() {

    this.filterDefinitions = <{ [N in ContentFilterKeyName]: IFilterDefinition<any> }>_.indexBy(
      _.compact([

        // General Filters

        <ITypeaheadFilterDefinition>
        {
          name: 'project',
          type: 'typeahead',
          category: 'general',
          field: FilterFieldNames.projectId,
          valueFromParams: this.fromSingleParam('project', (paramValue: string) => this.suggestions.project(paramValue)),
          valueToParams: this.toSingleParam('project', (fieldValue: IFieldValue) => fieldValue.value),
          matchPrefixesOnly: false
        },

        !this.ServerService.isLimitedUserVisibilityEnforced() && this.getContentOwnerFilter(),

        <ITypeaheadFilterDefinition>
        {
          name: 'tag',
          type: 'typeahead',
          category: 'general',
          field: FilterFieldNames.tags,
          valueFromParams: this.fromSingleParam('tag', (paramValue: string) => this.$q.when({ value: paramValue, label: paramValue, count: 0 })),
          valueToParams: this.toSingleParam('tag', (fieldValue: IFieldValue) => fieldValue.value),
          matchPrefixesOnly: false
        },

        this.siteRoleFilterDefinition(),
        this.groupsMinimumSiteRoleDefinition(),
        this.maxSiteRoleDefinition(),

        this.afterDateFilterDefinition('createdAfter', 'general'),
        this.beforeDateFilterDefinition('createdBefore', 'general'),

        this.afterDateFilterDefinition('createdTimeAfter', 'general'),
        this.beforeDateFilterDefinition('createdTimeBefore', 'general'),

        this.afterDateFilterDefinition('modifiedAfter', 'general'),
        this.beforeDateFilterDefinition('modifiedBefore', 'general'),

        this.afterDateFilterDefinition('updatedTimeAfter', 'general'),
        this.beforeDateFilterDefinition('updatedTimeBefore', 'general'),

        this.afterDateFilterDefinition('startedAfter', 'general'),
        this.beforeDateFilterDefinition('startedBefore', 'general'),
        this.afterDateFilterDefinition('completedAfter', 'general'),
        this.beforeDateFilterDefinition('completedBefore', 'general'),

        this.booleanFilterDefinition('favorite', 'general'),
        this.booleanFilterDefinition('recent', 'general'),
        this.booleanFilterDefinition('hasAlert', 'general'),
        FeatureFlags.personalSpaceEnabled && this.ServerService.isSessionSiteOrServerAdmin() && this.booleanFilterDefinition('usersPersonalSpaceContent', 'general'),

        <ITypeaheadFilterDefinition>
        {
          name: 'flowOutputStep',
          type: 'typeahead',
          category: 'general',
          field: FilterFieldNames.flowOutputStepName,
          valueFromParams: this.fromSingleParam('flowOutputStep', (paramValue: string) => this.$q.when({ value: paramValue, label: paramValue, count: 0 })),
          valueToParams: this.toSingleParam('flowOutputStep', (fieldValue: IFieldValue) => fieldValue.value),
          matchPrefixesOnly: false
        },

        this.selectFilterDefinition('status',
          {
            '':           '',
            'PENDING':    FlowOutputStepRunStatus.pending,
            'INPROGRESS': FlowOutputStepRunStatus.inProgress,
            'SUCCESS':    FlowOutputStepRunStatus.success,
            'FAILED':     FlowOutputStepRunStatus.failed,
// TODO tfs844917            'CANCELLED':  FlowOutputStepRunStatus.cancelled,
          },
          [
            { value: '',                                  translate: 'flowRunStatusAll' },
            { value: FlowOutputStepRunStatus.success,     translate: 'flowRunStatusSucceeded' },
            { value: FlowOutputStepRunStatus.failed,      translate: 'flowRunStatusFailed' },
            // TODO tfs844917            { value: FlowOutputStepRunStatus.cancelled,   translate: 'flowRunStatusCancelled'},
 // TODO tfs844917            { value: FlowOutputStepRunStatus.cancelled,   translate: 'flowRunStatusCancelled'},
            // TODO tfs844917            { value: FlowOutputStepRunStatus.cancelled,   translate: 'flowRunStatusCancelled'},
            { value: FlowOutputStepRunStatus.inProgress,  translate: 'flowRunStatusInProgress' },
            { value: FlowOutputStepRunStatus.pending,     translate: 'flowRunStatusPending' },
          ],
          'general'
        ),

        this.selectFilterDefinition('backgroundJobStatus',
          {
            '': '',
            'Pending': BackgroundJobStatus.Pending,
            'InProgress': BackgroundJobStatus.InProgress,
            'Completed': BackgroundJobStatus.Completed,
            'Failed': BackgroundJobStatus.Failed,
            'Cancelled': BackgroundJobStatus.Cancelled,
          },
          [
            { value: '', translate: 'backgroundJobStatusAll' },
            { value: BackgroundJobStatus.Pending, translate: 'backgroundJobStatusPending' },
            { value: BackgroundJobStatus.InProgress, translate: 'backgroundJobStatusInProgress' },
            { value: BackgroundJobStatus.Completed, translate: 'backgroundJobStatusCompleted' },
            { value: BackgroundJobStatus.Failed, translate: 'backgroundJobStatusFailed' },
            { value: BackgroundJobStatus.Cancelled, translate: 'backgroundJobStatusCancelled' },
          ],
          'general'
        ),

        this.selectFilterDefinition('dataAccelerationStatus',
          {
            '': '',
            'CanBeAccelerated': DataAccelerationStatus.CanBeAccelerated,
            'Accelerated': DataAccelerationStatus.Accelerated,
            'CannotBeAccelerated': DataAccelerationStatus.CannotBeAccelerated,
          },
          [
            { value: '', translate: 'dataAccelerationStatusAll' },
            { value: DataAccelerationStatus.CanBeAccelerated, translate: 'dataAccelerationStatusCanBeAccelerated' },
            { value: DataAccelerationStatus.Accelerated, translate: 'dataAccelerationStatusAccelerated' },
            { value: DataAccelerationStatus.CannotBeAccelerated, translate: 'dataAccelerationStatusCannotBeAccelerated' },
          ],
          'dataAcceleration'
        ),

        this.backgroundJobTaskTypeDefinition(),

        this.selectFilterDefinition('backgroundJobTimeRange',
        {
          '': '',
          '1': BackgroundJobTimeRange.One,
          '4': BackgroundJobTimeRange.Four,
          '8': BackgroundJobTimeRange.Eight,
          '12': BackgroundJobTimeRange.Twelve,
          '16': BackgroundJobTimeRange.Sixteen,
          '20': BackgroundJobTimeRange.Twenty,
        },
        [
          {value: BackgroundJobTimeRange.One, translate: 'backgroundJobTimeRange1h'},
          {value: BackgroundJobTimeRange.Four, translate: 'backgroundJobTimeRange4h'},
          {value: BackgroundJobTimeRange.Eight, translate: 'backgroundJobTimeRange8h'},
          {value: BackgroundJobTimeRange.Twelve, translate: 'backgroundJobTimeRange12h'},
          {value: BackgroundJobTimeRange.Sixteen, translate: 'backgroundJobTimeRange16h'},
          {value: BackgroundJobTimeRange.Twenty, translate: 'backgroundJobTimeRange20h'},
          {value: '', translate: 'backgroundJobTimeRange24h'},
        ],
        'general'
      ),


        this.booleanFilterDefinition('showSystemJobs', 'general'),

        this.selectFilterDefinition('collectionVisibility',
        {
          '': '',
          'public': CollectionVisibility.public,
          'private': CollectionVisibility.private,
        },
        [
          { value: '', translate: 'collectionVisibilityAllFilter' },
          { value: CollectionVisibility.public, translate: 'collectionVisibilityPublic' },
          { value: CollectionVisibility.private, translate: 'collectionVisibilityPrivate' }
        ],
        'general'
      ),

        // Data Source Filters

        this.selectFilterDefinition('showEmbedded',
          { // param key values mapped to value states
            '': DatasourceType.Published,
            'true': DatasourceType.All,
            'only': DatasourceType.Embedded
          },
          [ // Options
            { value: DatasourceType.All,       translate: 'datasourceTypeAll' },
            { value: DatasourceType.Published, translate: 'datasourceTypePublishedOnly' },
            { value: DatasourceType.Embedded,  translate: 'datasourceTypeEmbeddedOnly' }
          ],
          'datasource'
        ),

        this.selectFilterDefinition('hasActiveWarning',
          {
            '': '',
            'false': 'false',
            'true': 'true',
          },
          [
            { value: '',      translate: 'hasActiveWarningAll' },
            { value: 'false', translate: 'hasActiveWarningFalse' },
            { value: 'true',  translate: 'hasActiveWarningTrue' },
          ],
          'datasource' // Only used for External Assets (Data Catalog)
        ),

        this.selectFilterDefinition('hasActiveDataQualityWarning', // DQW Filter with crass search enabled
          {
            '': '',
            'false': 'false',
            'true': 'true',
          },
          [
            { value: '',      translate: 'hasActiveWarningAll' },
            { value: 'false', translate: 'hasActiveWarningFalse' },
            { value: 'true',  translate: 'hasActiveWarningTrue' },
          ],
          'datasource'
        ),

        <ITypeaheadFilterDefinition>
        {
          name: 'connectionType',
          type: 'typeahead',
          category: 'datasource',
          field: FilterFieldNames.connectionType,
          valueFromParams: this.fromSingleParam('connectionType', (paramValue: string) => this.suggestions.dataConnectionType(paramValue)),
          valueToParams: this.toSingleParam('connectionType', (fieldValue: IFieldValue) => fieldValue.value),
          matchPrefixesOnly: true
        },

        this.textFilterDefinition('serverName', 'datasource'),

        this.textDropdownFilterDefinition('serverPort',
          [
            { value: null, translate: 'serverPortAny' },
            { value: FilterValueConstants.ServerPortDefault, translate: 'serverPortDefault' }
          ],
          (val: string) => (val === FilterValueConstants.ServerPortDefault
                               || (/^\d*$/.exec(val) && parseInt(val, 10) <= 65535)),
          'datasource'
        ),

        this.textFilterDefinition('databaseUsername', 'datasource'),

        this.selectFilterDefinition('hasPassword',
          { // param key values mapped to value states
            '': PasswordStatus.Any,
            'true': PasswordStatus.Embedded,
            'false': PasswordStatus.NotEmbedded
          },
          [ // Options
            { value: PasswordStatus.Any,         translate: 'hasPasswordAny' },
            { value: PasswordStatus.Embedded,    translate: 'hasPasswordEmbedded' },
            { value: PasswordStatus.NotEmbedded, translate: 'hasPasswordNotEmbedded' }
          ],
          'datasource'
        ),

        !this.ServerService.isExtractEncryptionEnabledOmniSwitch() && this.booleanFilterDefinition('hasExtracts', 'datasource'),
        this.ServerService.isExtractEncryptionEnabledOmniSwitch() && this.selectFilterDefinition('extractStatus',
          {
            '': ExtractStatus.All,
            'live': ExtractStatus.Live,
            // 'published' intentionally missing, the server-side filter translator currently does not support it
            'allExtracts': ExtractStatus.AllExtracts,
            'unencryptedExtracts': ExtractStatus.UnencryptedExtracts,
            'encryptedExtracts': ExtractStatus.EncryptedExctacts,
            'currentlyEncrypting': ExtractStatus.CurrentlyEncrypting,
            'currentlyDecrypting': ExtractStatus.CurrentlyDecrypting
          },
          [
            // Options
            {value: ExtractStatus.All, translate: 'extractStatusAll'},
            {value: ExtractStatus.Live, translate: 'extractStatusLive'},
            {value: ExtractStatus.AllExtracts, translate: 'extractStatusExtracts'},
            {value: ExtractStatus.UnencryptedExtracts, translate: 'extractStatusUnencrypted'},
            {value: ExtractStatus.EncryptedExctacts, translate: 'extractStatusEncrypted'},
            {value: ExtractStatus.CurrentlyEncrypting, translate: 'extractStatusEncrypting'},
            {value: ExtractStatus.CurrentlyDecrypting, translate: 'extractStatusDecrypting'}
          ],
          'datasource'
        ),
        this.ServerService.isExtractEncryptionEnabledOmniSwitch() && this.selectFilterDefinition('workbookConnFilter',
          {
            '': ExtractStatus.All,
            'live': ExtractStatus.Live,
            'published': ExtractStatus.Published,
            'allExtracts': ExtractStatus.AllExtracts,
            'unencryptedExtracts': ExtractStatus.UnencryptedExtracts,
            'encryptedExtracts': ExtractStatus.EncryptedExctacts,
            'currentlyEncrypting': ExtractStatus.CurrentlyEncrypting,
            'currentlyDecrypting': ExtractStatus.CurrentlyDecrypting
          },
          [
            // Options
            {value: ExtractStatus.All, translate: 'extractStatusAll'},
            {value: ExtractStatus.Live, translate: 'extractStatusLive'},
            {value: ExtractStatus.AllExtracts, translate: 'extractStatusExtracts'},
            {value: ExtractStatus.Published, translate: 'workbookConnFilterPublished'},
            {value: ExtractStatus.UnencryptedExtracts, translate: 'extractStatusUnencrypted'},
            {value: ExtractStatus.EncryptedExctacts, translate: 'extractStatusEncrypted'},
            {value: ExtractStatus.CurrentlyEncrypting, translate: 'extractStatusEncrypting'},
            {value: ExtractStatus.CurrentlyDecrypting, translate: 'extractStatusDecrypting'}
          ],
          'datasource'
        ),
        this.ServerService.isExtractEncryptionEnabledOmniSwitch() && this.booleanFilterDefinition('includeLiveToDataEngine', 'datasource'),
        this.booleanFilterDefinition('isCertified', 'datasource')

      ]), 'name');
  }

  private siteRoleFilterDefinition() {
    const allSiteRolesExceptGuest = _.without(SiteRolesResource.instance.allOptions(), SiteRoles.Guest);
    return this.siteRoleSelectFilterDefinition('siteRole', allSiteRolesExceptGuest);
  }

  private groupsMinimumSiteRoleDefinition() {
    return this.siteRoleSelectFilterDefinition('groupsMinimumSiteRole', SiteRolesResource.instance.optionsForCurrentUserExcept(SiteRoles.ServerAdministrator));
  }

  private backgroundJobTaskTypeDefinition() {
    let paramToValueMap = {
      '': '',
      'Bridge': BackgroundJobTaskType.Bridge,
      'Extract': BackgroundJobTaskType.Extract,
      'Flow': BackgroundJobTaskType.Flow,
      'Subscription': BackgroundJobTaskType.Subscription,
      'System': BackgroundJobTaskType.System,
      'Encryption': BackgroundJobTaskType.Encryption,
      'Acceleration': BackgroundJobTaskType.Acceleration,
    };
    let options = [
      { value: '', translate: 'backgroundJobTaskTypeAll' },
      { value: BackgroundJobTaskType.Bridge, translate: 'backgroundJobTaskTypeBridge' },
      { value: BackgroundJobTaskType.Extract, translate: 'backgroundJobTaskTypeExtract' },
      { value: BackgroundJobTaskType.Flow, translate: 'backgroundJobTaskTypeFlow' },
      { value: BackgroundJobTaskType.Subscription, translate: 'backgroundJobTaskTypeSubscription' },
      { value: BackgroundJobTaskType.System, translate: 'backgroundJobTaskTypeSystem' },
      { value: BackgroundJobTaskType.Encryption, translate: 'backgroundJobTaskTypeEncryption' },
      { value: BackgroundJobTaskType.Acceleration, translate: 'backgroundJobTaskTypeAcceleration'},
    ];

    // Non server admins cannot see the System  jobs option
    if (!this.ServerService.isSessionServerAdmin()) {
      delete paramToValueMap.System;
      options = options.filter(o => o.value !== BackgroundJobTaskType.System);
    }
    return this.selectFilterDefinition('backgroundJobTaskType', paramToValueMap, options, 'general');
  }

  private maxSiteRoleDefinition() {
    return this.siteRoleSelectFilterDefinition('maxSiteRole', SiteRolesResource.instance.optionsForCurrentUser());
  }

  private siteRoleSelectFilterDefinition(name: ContentFilterKeyName, roles: string[]) {
    return this.selectFilterDefinition(name,

      // param key values mapped to value states
      // Map '' to "any role"
      _.extend({}, SiteRoles, { '': SiteRoleFilterHelper.AnyRole }),

      SiteRoleFilterHelper.siteRoleOptions(roles),
      'general'
    );
  }

  private selectFilterDefinition<TFilterValue>(
    name: ContentFilterKeyName,
    paramToValueMap: ISelectFilterDefinitionMap<TFilterValue>,
    options: ISelectFilterDefinitionOption<TFilterValue>[],
    category?: FilterCategory
  ): ISelectFilterDefinition<TFilterValue> {
    const valueToParamMap = Helpers.swapDict(paramToValueMap);
    return {
      name: name,
      type: 'select',
      category: category,
      valueFromParams: (params: any) => this.$q.when(paramToValueMap[params[name]] || paramToValueMap['']), //Bypass fromSingleParam to handle the undefined param case
      valueToParams: this.toSingleParam(name, (val: TFilterValue) => (valueToParamMap[val + ''] || null)),
      options: options
    };
  }

  private booleanFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<boolean> {
    return {
      name: name,
      type: 'boolean',
      category: category,
      valueFromParams: this.fromSingleParam(name, (paramValue: string) => this.$q.when(Helpers.toBoolean(paramValue))),
      valueToParams: this.toSingleParam(name, (value: boolean) => '' + value)
    };
  }

  private getContentOwnerFilter(): ITypeaheadFilterDefinition {
    return  {
      name: 'owner',
      type: 'typeahead',
      category: 'general',
      field: FilterFieldNames.ownerId,
      valueFromParams: this.fromSingleParam('owner', (paramValue: string) => this.suggestions.user(paramValue)),
      valueToParams: this.toSingleParam('owner', (fieldValue: IFieldValue) => fieldValue.value),
      matchPrefixesOnly: true
    };
  }

  private textFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<string> {
    return {
      name: name,
      type: 'text',
      category: category,
      valueFromParams: this.fromSingleParam(name, (paramValue: string) => this.$q.when(paramValue)),
      valueToParams: this.toSingleParam(name, (text: string) => text)
    };
  }

  private textDropdownFilterDefinition(
    name: ContentFilterKeyName,
    options: ITextDropdownOption[],
    isValid: (val: string) => boolean,
    category?: FilterCategory
  ): ITextDropdownFilterDefition<string> {
    return {
      name: name,
      type: 'textDropdown',
      category: category,
      valueFromParams: this.fromSingleParam(name, (paramValue: string) => this.$q.when(isValid(paramValue) ? paramValue : null)),
      valueToParams: this.toSingleParam(name, (text: string) => text),
      options: options,
      isValid: isValid
    };
  }

  private afterDateFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<Date> {
    return {
      name: name,
      type: 'date',
      category: category,
      valueFromParams: this.fromSingleParam(name, (paramValue: string) => this.dateFromISOString(paramValue)),
      valueToParams: this.toSingleParam(name, (date: Date) => DateHelpers.toISOString(date))
    };
  }

  private beforeDateFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<Date> {
    return {
      name: name,
      type: 'date',
      category: category,
      valueFromParams: this.fromSingleParam(name, (paramValue: string) => this.dateFromISOString(paramValue)),
      valueToParams: this.toSingleParam(name, (date: Date) => DateHelpers.toISOString(DateHelpers.endOfDay(date)))
    };
  }

  private dateFromISOString(paramValue: string): ng.IPromise<Date> {
    return this.$q.when(DateHelpers.midnight(DateHelpers.fromISOString(paramValue)));
  }

  private fromSingleParam<TFilterValue>(paramName: ContentFilterKeyName, valueFn: (paramValue: string) => ng.IPromise<TFilterValue>) {
    return (params: any) => params[paramName] ? valueFn(params[paramName]) : this.$q.when(null);
  }

  private toSingleParam<TFilterValue>(paramName: ContentFilterKeyName, paramValueFn: (value: TFilterValue) => string) {
    return (value: TFilterValue) => {
      const params: any = {};
      params[paramName] = value ? paramValueFn(value) : null;
      return params;
    };
  }

  public getFilterDefinitions(filterNames: ContentFilterKeyName[]): IFilterDefinition<any>[] {
    const defns = _.map(filterNames, (filterName: ContentFilterKeyName) => this.filterDefinitions[filterName]);
    return _.filter(defns, (defn: IFilterDefinition<any>) => defn != null);
  }

}

Module.instance().service('FilterPanelService', FilterPanelService);
